import { useRef, useState } from "react";
import { UserCheck } from "lucide-react";
import { toast } from "sonner";
import { useIsFetching, useQueryClient } from "@tanstack/react-query";

import { checkOperation } from "@workspace/operations";
import type {
	Product,
	ProductIngredient,
} from "@workspace/product-catalog-types";
import { genid } from "@workspace/shared/utils";
import { Button } from "@workspace/ui/components/button";
import { cn } from "@workspace/ui/lib/utils";
import {
	pinFailureMessage,
	staffPortalApi,
} from "@workspace/venue-management-staff";
import type { StaffMember } from "@workspace/venue-management-staff/types";

import { kioskApi } from "../../api";
import { useKioskContext } from "../../kiosk";
import type { Order, OrderItem, SelectedOptionGroup } from "../../types";
import { useEposContext } from "../epos-context";
import { needsOptionsOnAdd } from "../options-on-add";
import { switchWithPin } from "../staff-token-store";
import { EposHeaderButtons } from "./epos-header";
import { EposProducts } from "./epos-products";
import { OpenCashDrawerDialog } from "./open-cash-drawer-dialog";
import { OrderItemOptionsEditor } from "./order-item-options-editor";
import { OrdersListView } from "./orders-list-view";
import { PaymentView } from "./payment-view";
import { RefundHistoryView } from "./refund-history-view";
import { SettingsView } from "./settings-view";
import { StaffPinDialog } from "./staff-pin-dialog";
import { StaffSelection } from "./staff-selection";

export function EposLeftPanel() {
	const {
		view,
		selectedStaff,
		operations,
		currentOrder,
		createNewOrder,
		setSelectedStaff,
		setView,
		currentMenu,
		addItemToOrder,
		// stock: { data: stockData },
		timeZone,
		currencies,
		tenant,
		data,

		shifts,
		staffMembers,
		switchOrder,

		isOpenDrawerDialogOpen,
		setIsOpenDrawerDialogOpen,

		setActiveOrderId,
		setActiveTabId,
		getAvailableOptions,
	} = useEposContext();

	const { setShowCheckInOut, venueId } = useKioskContext();

	// HA-663 — the picker's own way out. The kiosk document is fed by an Async projection, so a
	// check-in that has already committed can be moments away from appearing; this asks again now
	// rather than leaving the operator waiting on the poll.
	const queryClient = useQueryClient();
	const kioskKey = [tenant, venueId, "kiosk"];
	const isRefreshingStaff = useIsFetching({ queryKey: kioskKey }) > 0;

	// HA-831 part 2: a tap asks for the PIN; only the PIN starts the staff session.
	const [pinFor, setPinFor] = useState<StaffMember | null>(null);
	const [pinError, setPinError] = useState<string | undefined>();
	const [isSwitching, setIsSwitching] = useState(false);

	/**
	 * HA-1025 / D-HA-72 — a product with a required option group is asked for its options before it
	 * is added: this is the tap waiting on them. Quantity is the long-press count: one popup per item
	 * of it (D-HA-74, 74-6), each Save adding one line.
	 */
	const [pendingAdd, setPendingAdd] = useState<{
		product: Product;
		variantId: string;
		quantity: number;
		/** Which item of the quantity this popup is for, from 1. */
		index: number;
		draft: OrderItem;
	} | null>(null);
	// Set at once, where the state shows on the next render: a second tap in between is the same tap.
	const asking = useRef(false);
	// The next popup of a long-press, held until this one closes: Save leaves it for the editor's close,
	// Cancel leaves none, so Cancel stops the run.
	const nextAdd = useRef<typeof pendingAdd>(null);

	const buildLine = (
		product: Product,
		variantId: string,
		options?: {
			selectedOptions: SelectedOptionGroup[];
			notes: string;
			removedIngredients: ProductIngredient[];
		},
	): OrderItem | null => {
		if (!currentMenu) return null;
		const variant = product.variants?.find((v) => v.id === variantId);
		if (!variant) {
			toast.error("Please select a valid product variant");
			return null;
		}
		return {
			id: `ITEM-${genid()}`, // Ensure unique ID
			productId: product.id,
			name: variant.name,
			quantity: 1,
			status: "Pending",
			sentToKitchen: false,
			productVariantId: variantId,
			price: variant.salePrice || product.basePrice || currencies.money(0),
			menuId: currentMenu.id,
			...(options && {
				selectedOptions: options.selectedOptions,
				notes: options.notes || undefined,
				removedIngredients: options.removedIngredients,
			}),
		};
	};

	// Add item to order
	const addProduct = (product: Product, variantId: string, quantity = 1) => {
		if (!currentMenu) return null;

		if (currentOrder && currentOrder.status !== "Open") {
			toast.error("Cannot add items to a closed order");
			return;
		}

		const draft = buildLine(product, variantId);
		if (!draft) return;

		if (needsOptionsOnAdd(getAvailableOptions(draft))) {
			if (asking.current) return;
			asking.current = true;
			setPendingAdd({ product, variantId, quantity, index: 1, draft });
			return;
		}

		addLines(product, variantId, quantity);
	};

	const addLines = (
		product: Product,
		variantId: string,
		quantity: number,
		options?: {
			selectedOptions: SelectedOptionGroup[];
			notes: string;
			removedIngredients: ProductIngredient[];
		},
	) => {
		if (!currentMenu) return;

		if (!currentOrder) {
			return createNewOrder({
				product,
				variantId,
				menuId: currentMenu.id,
				quantity,
				options,
			});
		}

		for (let i = 0; i < quantity; i++) {
			const newItem = buildLine(product, variantId, options);
			if (!newItem) return;

			addItemToOrder(currentOrder.id, newItem);
		}
	};

	const closeOptionsOnAdd = () => {
		const next = nextAdd.current;
		nextAdd.current = null;
		if (next) {
			setPendingAdd(next);
			return;
		}
		asking.current = false;
		setPendingAdd(null);
	};

	const handleStaffSelect = (staff: StaffMember) => {
		setPinError(undefined);
		setPinFor(staff);
	};

	const handlePin = async (pin: string) => {
		const staff = pinFor;
		if (!staff) return;

		setIsSwitching(true);
		let result;
		try {
			// HA-831: the PIN proves the switch; the session is ended again if they may not use the EPOS.
			result = await switchWithPin({
				staff,
				pin,
				checkPin: (input) => staffPortalApi.checkPin(tenant, venueId, input),
				start: (input) =>
					staffPortalApi.startStaffSession(tenant, venueId, input),
				mayUseEpos: async () =>
					checkOperation({
						operation: "staff-terminal-epos",
						operations: await kioskApi.getUserOperations(
							tenant,
							venueId,
							staff.registeredUserId,
						),
					}),
				tenant,
				venueId,
			});
		} finally {
			setIsSwitching(false);
		}

		if (result.outcome === "pin-failed") {
			setPinError(pinFailureMessage(result.failure));
			return;
		}

		setPinFor(null);

		if (result.outcome === "not-started") {
			toast.error("Failed to activate staff session");
			return;
		}

		if (result.outcome === "refused") {
			toast.error(
				"You don't have the correct permissions to access this area.",
			);
			return;
		}

		setSelectedStaff(staff);
		switchOrder(staff);
		setView("products");
	};

	const handleOpenDrawer = () => {
		setIsOpenDrawerDialogOpen(true);
	};

	const currentDrawerId = data.currentCashDrawer.id || "DRAWER-01";

	return (
		<div
			className={cn("flex h-full flex-col md:col-span-2", {
				"md:col-span-3": !selectedStaff,
			})}
		>
			<div className="max-h-[calc(var(--kiosk-vh,100vh)-87px)] flex-1 overflow-auto rounded-md border shadow-sm">
				{!selectedStaff && (
					<div className="flex h-full flex-col gap-4 p-4">
						<Button
							variant="outline"
							size="lg"
							onClick={() => setShowCheckInOut(true)}
							className="flex items-center gap-1 bg-primary-foreground"
						>
							<UserCheck className="size-4" />
							<span className="hidden sm:inline">Check In/Out</span>
						</Button>
						<StaffPinDialog
							staff={pinFor}
							error={pinError}
							isSubmitting={isSwitching}
							onSubmit={(pin) => void handlePin(pin)}
							onCancel={() => setPinFor(null)}
						/>
						<StaffSelection
							staffMembers={staffMembers}
							shifts={shifts}
							onSelectStaff={handleStaffSelect}
							onRefresh={() =>
								void queryClient.invalidateQueries({ queryKey: kioskKey })
							}
							isRefreshing={isRefreshingStaff}
						/>
					</div>
				)}

				{/* Products View with Categories and Items. A selected staff member on the staff view is
				    shown the menu too (HA-1023): that state has no screen of its own. */}
				{selectedStaff && (view === "products" || view === "staff") && (
					<EposProducts onProductSelect={addProduct} />
				)}

				{/* Tables View */}

				{/* Settings View */}
				{selectedStaff && view === "settings" && <SettingsView />}

				{/* Payment View */}
				{selectedStaff && view === "payment" && currentOrder && <PaymentView />}

				{/* Orders List View */}
				{selectedStaff && view === "orders" && (
					<OrdersListView onClose={() => setView("products")} />
				)}

				{/* Refund History View */}
				{selectedStaff && view === "refund-history" && (
					<RefundHistoryView
						onClose={() => setView("products")}
						onSelectOrder={(order: Order) => {
							// Find the order in active orders and select it for refund
							const targetOrder = data.orders.find((o) => o.id === order.id);

							if (targetOrder) {
								setActiveOrderId(targetOrder.id);
								if (targetOrder.tabId) setActiveTabId(targetOrder.tabId);
							}
							setView("products");
						}}
					/>
				)}
			</div>
			<div className="flex justify-between pt-2">
				<EposHeaderButtons
					operations={operations}
					timeZone={timeZone}
					staffMember={selectedStaff}
					currentView={view}
					onStaffSwitch={() => {
						setView("staff");
						setSelectedStaff(null);
					}}
					onViewChange={setView}
					onOpenDrawer={handleOpenDrawer}
					stockStatus="Ok"
					tenant={tenant}
					// stockStatus={
					// 	stockData?.items.some((item) => item.status !== "Ok") ? "Low" : "Ok"
					// }
				/>
			</div>

			<OpenCashDrawerDialog
				isOpen={isOpenDrawerDialogOpen}
				onOpenChange={setIsOpenDrawerDialogOpen}
				drawerId={currentDrawerId}
			/>

			{/* HA-1025 / D-HA-72 — the options of a product with a required group, before it is added.
			    Save adds one line and asks for the next item of a long-press, pre-filled from this one;
			    Cancel adds nothing and ends the run (D-HA-74, 74-6). */}
			{pendingAdd && (
				<OrderItemOptionsEditor
					key={pendingAdd.index}
					isOpen
					mode="add"
					onClose={closeOptionsOnAdd}
					orderItem={pendingAdd.draft}
					availableOptions={getAvailableOptions(pendingAdd.draft)}
					position={
						pendingAdd.quantity > 1
							? { index: pendingAdd.index, total: pendingAdd.quantity }
							: undefined
					}
					onSave={(_itemId, selectedOptions, notes, removedIngredients) => {
						const options = { selectedOptions, notes, removedIngredients };
						addLines(pendingAdd.product, pendingAdd.variantId, 1, options);
						if (pendingAdd.index < pendingAdd.quantity) {
							const draft = buildLine(
								pendingAdd.product,
								pendingAdd.variantId,
								options,
							);
							if (draft) {
								nextAdd.current = {
									...pendingAdd,
									index: pendingAdd.index + 1,
									draft,
								};
							}
						}
					}}
					product={pendingAdd.product}
				/>
			)}
		</div>
	);
}
